import { IconButton } from '@atlaskit/button/new';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import DropdownMenu, { type OnOpenChangeArgs } from '@databases/atlaskit/DropdownMenu';
import { messages } from '@databases/intl/messages';
import composeRefs from '@seznam/compose-react-refs';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { useFormatRelativeTime } from '@canvas/intl/format-relative-time';

import { AiSummaryRunDropdownItems } from './ai-summary-run-dropdown-items';
import { useIsAiSummaryFieldEnabled } from './use-is-ai-summary-field-enabled';

export interface AiSummaryFieldHeaderActionProps {
  confirmationKey: string;
  hasUserEditedCells: () => boolean;
  lastRunAt?: string;
  onRerunEntireColumn: () => void;
  onRerunEmptyEntries: () => void;
}

export const AiSummaryFieldHeaderAction = ({
  confirmationKey,
  hasUserEditedCells,
  lastRunAt,
  onRerunEntireColumn,
  onRerunEmptyEntries
}: AiSummaryFieldHeaderActionProps) => {
  const { formatMessage } = useIntl();
  const relativeLastRunTime = useFormatRelativeTime(lastRunAt);
  const [isOpen, setIsOpen] = useState(false);
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const tooltipContent = relativeLastRunTime
    ? formatMessage(messages['field-actions.ai-summary-rerun-tooltip'], {
        relativeTime: relativeLastRunTime
      })
    : formatMessage(messages['field-actions.ai-summary-run-tooltip']);

  const handleOpenChange = useCallback(({ isOpen }: OnOpenChangeArgs) => {
    setIsOpen(isOpen);
  }, []);

  if (!isAiFieldsSummaryEnabled) {
    return null;
  }

  return (
    <Box testId="ai-summary-field-header-action">
      <DropdownMenu
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        spacing="compact"
        placement="bottom-end"
        testId="ai-summary-field-header-action-menu"
        trigger={({ triggerRef, ...triggerProps }) => (
          <Tooltip
            content={tooltipContent}
            position="top"
            hideTooltipOnClick
            // Prevent Tooltip's hidden announcement from affecting the inline header layout.
            isScreenReaderAnnouncementDisabled
          >
            {(tooltipProps) => {
              const { onClick, ...restTriggerProps } = triggerProps;

              return (
                <IconButton
                  {...tooltipProps}
                  {...restTriggerProps}
                  appearance="subtle"
                  icon={(iconProps) => (
                    <AiSparkleIcon {...iconProps} size="small" spacing="compact" />
                  )}
                  isSelected={isOpen}
                  label={formatMessage(
                    messages['field-actions.ai-summary-refresh.non-final']
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    tooltipProps.onClick(event);
                    onClick?.(event);
                  }}
                  ref={composeRefs(triggerRef, tooltipProps.ref)}
                  spacing="compact"
                  testId="ai-summary-field-header-action-trigger"
                />
              );
            }}
          </Tooltip>
        )}
      >
        <AiSummaryRunDropdownItems
          confirmationKey={confirmationKey}
          hasUserEditedCells={hasUserEditedCells}
          onRerunEntireColumn={onRerunEntireColumn}
          onRerunEmptyEntries={onRerunEmptyEntries}
        />
      </DropdownMenu>
    </Box>
  );
};
